import React from 'react';
import { Subtitle } from '@trevor/ui-components';
import {
  GET_CREDITS_CONSUMPTION_DETAILS,
  usePrefetch,
} from '@/features/credits/apiSlice';
import { useDatasourceId, useToggle } from '@/hooks';
import {
  ActivityContainer,
  ActivityExpandIcon,
  ActivityHeader,
} from '@/features/credits/components/HowYouUseCredits/components/Activity/Activity.styles';
import { useIsActivityHighlighted } from './hooks/useIsActivityHighlighted';
import { ActivityInfo } from './components/ActivityInfo';
import {
  TConsumptionCategory,
  TConsumptionCategoryAndType,
} from '../../../../types';
import {
  CONSUMPTION_CATEGORY_TO_COLORS,
  CONSUMPTION_CATEGORY_TYPE_TO_DISPLAY_NAME,
} from '../../../../config';

export type ActivityProps = {
  category: TConsumptionCategory;
  type: string;
  consumption: number;
  totalCredits: number;
};

export const Activity: React.FC<ActivityProps> = ({
  category,
  type,
  consumption,
  totalCredits,
}) => {
  const prefetchDetails = usePrefetch(GET_CREDITS_CONSUMPTION_DETAILS);
  const datasourceId = useDatasourceId();
  const isHighlighted = useIsActivityHighlighted(category);

  const [isActivityInfoOpen, toggleActivityInfo] = useToggle(false, true);

  const handlePrefetchDetails = () =>
    prefetchDetails({
      datasourceId,
      category,
      type,
    });

  const colors = CONSUMPTION_CATEGORY_TO_COLORS[category];
  const translationKey = [...new Set([category, type])].join(
    ':',
  ) as TConsumptionCategoryAndType;

  return (
    <ActivityContainer color={colors.main}>
      <ActivityHeader
        onClick={toggleActivityInfo}
        onMouseEnter={handlePrefetchDetails}
        color={isHighlighted ? colors.highlight : undefined}
        hasBottomRightBorderRadius={!isActivityInfoOpen}
      >
        <Subtitle variant={2} weight="bold">
          {CONSUMPTION_CATEGORY_TYPE_TO_DISPLAY_NAME[translationKey]}
        </Subtitle>
        <Subtitle variant={2}>
          {Math.round((consumption * 100) / totalCredits)}%
        </Subtitle>
        <ActivityExpandIcon flip={isActivityInfoOpen ? 1 : 0} />
      </ActivityHeader>
      {isActivityInfoOpen && (
        <ActivityInfo
          category={category}
          type={type}
          consumption={consumption}
        />
      )}
    </ActivityContainer>
  );
};
